import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QuestionAnswer as FAQIcon,
} from '@mui/icons-material';
import apiService from '../services/apiService';

const FAQsPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState('all');

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    userType: 'driver',
    sortOrder: 0,
    status: 'active',
  });
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingFaq, setDeletingFaq] = useState(null);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getFAQs({
        userType: tabValue === 'all' ? undefined : tabValue,
      });
      if (response.success) {
        setFaqs(response.data);
      }
    } catch (err) {
      setError('Failed to fetch FAQs');
      console.error('Fetch FAQs error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const handleOpenDialog = (faq = null) => {
    if (faq) {
      setEditingFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        userType: faq.user_type,
        sortOrder: faq.sort_order || 0,
        status: faq.status,
      });
    } else {
      setEditingFaq(null);
      setFormData({
        question: '',
        answer: '',
        userType: 'driver',
        sortOrder: 0,
        status: 'active',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFaq(null);
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      setError('Question and answer are required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingFaq) {
        // Update
        await apiService.updateFAQ(editingFaq.id, formData);
        setSuccess('FAQ updated successfully');
      } else {
        // Create
        await apiService.addFAQ(formData);
        setSuccess('FAQ created successfully');
      }

      handleCloseDialog();
      fetchFAQs();
    } catch (err) {
      setError(err.message || 'Failed to save FAQ');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (faq) => {
    setDeletingFaq(faq);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingFaq) return;

    try {
      await apiService.deleteFAQ(deletingFaq.id);
      setSuccess('FAQ deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingFaq(null);
      fetchFAQs();
    } catch (err) {
      setError(err.message || 'Failed to delete FAQ');
    }
  };

  const getUserTypeColor = (type) => {
    switch (type) {
      case 'driver':
        return 'primary';
      case 'passenger':
        return 'secondary';
      case 'both':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FAQIcon sx={{ fontSize: 32, color: '#FFC107' }} />
          <Typography variant="h4" fontWeight="bold">
            FAQs Management
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#FFC107',
            color: '#000',
            '&:hover': { bgcolor: '#FFD54F' },
          }}
        >
          Add FAQ
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root.Mui-selected': { color: '#FFC107' },
            '& .MuiTabs-indicator': { bgcolor: '#FFC107' },
          }}
        >
          <Tab label="All FAQs" value="all" />
          <Tab label="Driver FAQs" value="driver" />
          <Tab label="Passenger FAQs" value="passenger" />
          <Tab label="Both" value="both" />
        </Tabs>
      </Paper>

      {/* FAQs Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#FFC107' }} />
          </Box>
        ) : faqs.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="textSecondary">No FAQs found</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Answer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>User Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqs.map((faq) => (
                <TableRow key={faq.id} hover>
                  <TableCell>{faq.sort_order}</TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                    <Typography variant="body2" noWrap title={faq.question}>
                      {faq.question}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap title={faq.answer}>
                      {faq.answer}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={faq.user_type.charAt(0).toUpperCase() + faq.user_type.slice(1)}
                      color={getUserTypeColor(faq.user_type)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={faq.status.charAt(0).toUpperCase() + faq.status.slice(1)}
                      color={getStatusColor(faq.status)}
                      size="small"
                      variant={faq.status === 'active' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(faq)}
                      sx={{ color: '#1976d2' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(faq)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Question"
              fullWidth
              required
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="Enter the FAQ question"
            />
            <TextField
              label="Answer"
              fullWidth
              required
              multiline
              rows={4}
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              placeholder="Enter the answer"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.userType}
                  label="User Type"
                  onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                >
                  <MenuItem value="driver">Driver</MenuItem>
                  <MenuItem value="passenger">Passenger</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Sort Order"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                sx={{ width: 150 }}
              />
              <FormControl sx={{ width: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              bgcolor: '#FFC107',
              color: '#000',
              '&:hover': { bgcolor: '#FFD54F' },
            }}
          >
            {saving ? <CircularProgress size={24} /> : (editingFaq ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this FAQ?
          </Typography>
          {deletingFaq && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              "{deletingFaq.question}"
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FAQsPage;
